/**
 * Console-only error reporting. The upstream project shipped Sentry here;
 * OKB Me keeps the same call sites (reportError / markExportStarted / …) but
 * routes everything to the console — no third-party telemetry.
 */

/** Marker set while an export runs; still present at boot = the page died
 * mid-export (tab crash / out-of-memory kill — no JS error ever fires). */
const EXPORT_MARKER_KEY = 'okbMe.exportInFlight'

export function markExportStarted(info: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(EXPORT_MARKER_KEY, JSON.stringify({ ...info, startedAt: Date.now() }))
  } catch {
    // Storage unavailable — we just lose this diagnostic.
  }
}

export function clearExportMarker(): void {
  try {
    sessionStorage.removeItem(EXPORT_MARKER_KEY)
  } catch {
    // Ignore.
  }
}

export function initErrorReporting(): void {
  try {
    const raw = sessionStorage.getItem(EXPORT_MARKER_KEY)
    if (raw) {
      sessionStorage.removeItem(EXPORT_MARKER_KEY)
      console.warn('Previous session died mid-export (likely OOM/crash)', JSON.parse(raw))
    }
  } catch {
    // Ignore.
  }
}

/** True for expected product gates that must never become crash reports. */
export function isExpectedUserError(error: unknown): boolean {
  return error instanceof Error && error.name === 'ProjectLimitError'
}

/**
 * Explicit capture for errors we catch and surface as in-app messages
 * (export error sheet, import error banner, …). The step keeps the console
 * output searchable the way the old Sentry tag was.
 */
export function reportError(
  error: unknown,
  step: string,
  extra?: Record<string, unknown>,
): void {
  if (isExpectedUserError(error)) return
  console.error(`[${step}]`, error, extra ?? '')
}

/**
 * Remix component errors surface on the virtual root's `error` event instead
 * of window.onerror. The mechanism: main.tsx wires
 * `root.addEventListener('error', …)` to this.
 */
export function reportComponentError(error: unknown): void {
  console.error('Uncaught component error', error)
}
